import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { API } from '../App';
import { useAuthedImage } from '../hooks/useAuthedImage';
import './GaitAnalysisPage.css';

// ─── Helpers ────────────────────────────────────────────────────────────────

const buildChartData = (timeseries, keys) => {
  if (!timeseries || !timeseries.t) return [];
  return timeseries.t.map((t, i) => {
    const row = { t: Number(t.toFixed(2)) };
    keys.forEach(k => { row[k] = timeseries[k] ? timeseries[k][i] : null; });
    return row;
  });
};

// Construit un diagramme de phases d'appui (stance=1 / oscillation=0) G & D
// à partir des contacts talon détectés, en estimant la durée d'appui comme
// ~62% de la durée de pas (valeur typique en analyse de la marche).
const buildPhaseData = (timeseries, contacts, stancePercent = 0.62) => {
  if (!timeseries?.t || !contacts?.length) return [];

  const bySide = { left: [], right: [] };
  contacts.forEach(c => { if (bySide[c.side]) bySide[c.side].push(c.t); });

  const strideDuration = (times) => {
    if (times.length < 2) return null;
    const diffs = times.slice(1).map((t, i) => t - times[i]);
    return diffs.reduce((a, b) => a + b, 0) / diffs.length;
  };

  const durLeft  = strideDuration(bySide.left);
  const durRight = strideDuration(bySide.right);

  const isStance = (t, contactTimes, dur) => {
    if (!dur) return 0;
    for (const c of contactTimes) {
      if (t >= c && t < c + dur * stancePercent) return 1;
    }
    return 0;
  };

  return timeseries.t.map(t => ({
    t: Number(t.toFixed(2)),
    stance_left: isStance(t, bySide.left, durLeft),
    stance_right: isStance(t, bySide.right, durRight),
  }));
};

const formatDuration = (ms) => (ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`);

// ─── Composants ─────────────────────────────────────────────────────────────

function UploadSlot({ label, hint, value, onChange, t }) {
  const inputRef = useRef();
  return (
    <div className={`gait-upload-slot ${value ? 'has-file' : ''}`}>
      <input
        ref={inputRef}
        type="file"
        accept="video/*"
        style={{ display: 'none' }}
        onChange={e => onChange(e.target.files[0] || null)}
      />
      <button className="gait-slot-btn" onClick={() => inputRef.current.click()}>
        <span className="gait-slot-icon">{value ? '🎬' : '📹'}</span>
        <span className="gait-slot-label">{label}</span>
        <span className="gait-slot-hint">{value ? value.name : hint}</span>
      </button>
      <div className="gait-slot-footer">
        <span className={`gait-slot-status ${value ? 'ok' : 'optional'}`}>
          {value ? t('gait.slot_added') : t('gait.slot_optional')}
        </span>
        {value && (
          <button className="gait-slot-remove" onClick={() => onChange(null)}>
            {t('gait.slot_remove')}
          </button>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, unit, warn }) {
  return (
    <div className={`gait-stat-card ${warn ? 'warn' : ''}`}>
      <span className="gait-stat-value">{value}<span className="gait-stat-unit">{unit}</span></span>
      <span className="gait-stat-label">{label}</span>
    </div>
  );
}

function GaitChart({ title, data, lines, refLines = [], yDomain = ['auto', 'auto'], yTicks, stepped = false, t }) {
  return (
    <div className="gait-chart-card">
      <div className="gait-chart-title">{title}</div>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: -10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2a2a4a" />
          <XAxis dataKey="t" stroke="#888" fontSize={11}
            label={{ value: t('gait.time_axis'), position: 'insideBottom', offset: -2, fontSize: 10, fill: '#888' }} />
          <YAxis stroke="#888" fontSize={11} domain={yDomain} ticks={yTicks} />
          <Tooltip
            contentStyle={{ background: '#16213e', border: '1px solid #2a2a4a', fontSize: 12 }}
            labelStyle={{ color: '#ccc' }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {refLines.map((y, i) => (
            <ReferenceLine key={i} y={y} stroke="#883333" strokeDasharray="3 3" />
          ))}
          {lines.map(l => (
            <Line key={l.key} type={stepped ? 'stepAfter' : 'monotone'} dataKey={l.key} name={l.name}
              stroke={l.color} strokeWidth={stepped ? 2 : 1.6} dot={false} connectNulls />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function VideoSection({ pid, view, title, subtitle, summaryStats, charts, t, qs }) {
  // <video src=...> ne peut pas envoyer le jeton d'authentification --
  // fetch() (intercepté globalement) + URL objet locale à la place.
  const { blobUrl } = useAuthedImage(`${API}/api/patients/${pid}/gait-video/${view}${qs || ''}`);
  return (
    <section className="gait-section">
      <div className="gait-section-header">
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>

      <div className="gait-section-body">
        <div className="gait-video-wrap">
          {blobUrl && (
            <video
              className="gait-video"
              controls
              src={blobUrl}
            />
          )}
        </div>

        <div className="gait-stats-grid">
          {summaryStats.map(s => <StatCard key={s.label} {...s} />)}
        </div>
      </div>

      <div className="gait-charts-grid">
        {charts.map(c => <GaitChart key={c.title} {...c} t={t} />)}
      </div>
    </section>
  );
}

// ─── Capture live WebRTC ────────────────────────────────────────────────────

function LiveCaptureSection({ patientId, t, onClose }) {
  const localVideoRef  = useRef(null);
  const remoteVideoRef = useRef(null);
  const pcRef = useRef(null);
  const streamRef = useRef(null);

  const [status, setStatus] = useState('idle'); // idle | connecting | live | error
  const [error, setError] = useState('');

  const start = async () => {
    setStatus('connecting');
    setError('');
    try {
      // Tentative stricte caméra arrière (exact, pas ideal — Safari iOS ignore
      // souvent 'ideal' et retombe sur la frontale silencieusement). En cas
      // d'échec (desktop, appareil mono-caméra), on retombe sur la caméra par
      // défaut sans contrainte de facingMode.
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 320, height: 240, frameRate: 10, facingMode: { exact: 'environment' } },
          audio: false,
        });
      } catch (e) {
        console.log('[GAIT-LIVE] caméra arrière indisponible, repli caméra par défaut:', e.message);
        stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 320, height: 240, frameRate: 10 },
          audio: false,
        });
      }
      streamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      // Récupérer les credentials TURN (Twilio) depuis le backend.
      // IMPORTANT : le navigateur n'est PAS sur Azure, il n'est donc PAS bloqué
      // en UDP sortant. On lui laisse TOUS les transports TURN (notamment
      // turn:3478?transport=udp), car l'allocation d'un relais en UDP est bien
      // plus fiable que TURN-over-TCP (qui échouait souvent → 0 candidat relais
      // → "connexion perdue"). Le filtrage tcp/443 ne concerne QUE le serveur
      // (cf. backend), où Azure Container Apps bloque l'UDP/TURN-TCP sortant.
      // Combiné à iceTransportPolicy:'relay', le navigateur produit quand même
      // uniquement des candidats relais — mais via le transport qui marche.
      let iceServers = [{ urls: 'stun:stun.l.google.com:19302' }];
      try {
        const turnRes = await fetch(`${API}/api/gait-live/turn-credentials`);
        if (turnRes.ok) {
          const turnData = await turnRes.json();
          if (turnData.iceServers?.length) {
            iceServers = turnData.iceServers.map(s => ({
              urls: s.urls || s.url,
              username: s.username,
              credential: s.credential,
            }));
            console.log('[GAIT-LIVE] ICE servers Twilio reçus:', turnData.iceServers);
            console.log('[GAIT-LIVE] ICE servers navigateur (tous transports):', iceServers);
          }
        }
      } catch {}

      // iceTransportPolicy: 'relay' → le navigateur ne génère QUE des candidats
      // relais TURN. Sur Azure Container Apps, le serveur n'a aucun candidat
      // host/srflx joignable de l'extérieur : la seule paire qui peut s'établir
      // est relais(navigateur) ↔ relais(serveur). Forcer relay-only élimine les
      // candidats host/srflx inutiles qui faisaient échouer/traîner la phase ICE.
      const pc = new RTCPeerConnection({ iceServers, iceTransportPolicy: 'relay' });
      pcRef.current = pc;

      // Collecte manuelle des candidats au fur et à mesure qu'ils arrivent.
      // On ne fait JAMAIS confiance à pc.localDescription pour contenir les
      // candidats à jour : on les construit nous-mêmes dans gatheredCandidates,
      // puis on les injecte explicitement dans le SDP avant envoi au serveur.
      const gatheredCandidates = [];
      let gatheringDone = false;

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          gatheredCandidates.push(event.candidate.candidate);
          console.log('[GAIT-LIVE] candidat reçu:', event.candidate.candidate);
        } else {
          gatheringDone = true;
          console.log('[GAIT-LIVE] fin de collecte, total candidats:', gatheredCandidates.length);
        }
      };

      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      pc.ontrack = (event) => {
        if (remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = event.streams[0];
        }
      };

      pc.onconnectionstatechange = () => {
        if (pc.connectionState === 'connected') setStatus('live');
        if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
          setStatus('error');
          setError(t('gait.live_error'));
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Attente active de la fin de collecte (max 8s), basée sur notre propre
      // flag gatheringDone (mis à jour via onicecandidate), plus fiable que
      // pc.iceGatheringState dans certains cas observés en pratique.
      const waitUntil = Date.now() + 8000;
      while (!gatheringDone && Date.now() < waitUntil) {
        await new Promise(r => setTimeout(r, 100));
      }

      // On reconstruit nous-mêmes le SDP final en y injectant les candidats
      // collectés manuellement si jamais pc.localDescription.sdp ne les
      // contient pas déjà (comportement observé sur certaines configurations).
      let finalSdp = pc.localDescription.sdp;
      const sdpHasCandidates = finalSdp.includes('a=candidate');
      if (!sdpHasCandidates && gatheredCandidates.length) {
        const lines = finalSdp.split('\r\n');
        const mLineIndex = lines.findIndex(l => l.startsWith('m='));
        const insertAt = lines.findIndex((l, i) => i > mLineIndex && l.startsWith('a=mid'));
        // event.candidate.candidate contient DÉJÀ le préfixe "candidate:",
        // donc on préfixe seulement avec "a=" (pas "a=candidate:").
        const candidateLines = gatheredCandidates.map(c => `a=${c}`);
        lines.splice(insertAt + 1, 0, ...candidateLines, 'a=end-of-candidates');
        finalSdp = lines.join('\r\n');
        console.log('[GAIT-LIVE] candidats injectés manuellement dans le SDP');
      }
      console.log('[GAIT-LIVE] SDP final envoyé contient des candidats:', finalSdp.includes('a=candidate'));

      const res = await fetch(`${API}/api/patients/${patientId}/gait-live/offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sdp: finalSdp, type: pc.localDescription.type }),
      });
      if (!res.ok) throw new Error(t('gait.live_error_server'));
      const answer = await res.json();

      await pc.setRemoteDescription(new RTCSessionDescription(answer));
    } catch (e) {
      setStatus('error');
      setError(e.message || t('gait.live_error_camera'));
    }
  };

  const stop = async () => {
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(tr => tr.stop());
      streamRef.current = null;
    }
    try {
      await fetch(`${API}/api/gait-live/close`, { method: 'POST' });
    } catch {}
    setStatus('idle');
  };

  const handleClose = async () => {
    await stop();
    onClose();
  };

  return (
    <section className="gait-live-section">
      <div className="gait-section-header">
        <h2>{t('gait.live_title')}</h2>
        <p>{t('gait.live_intro')}</p>
      </div>

      <div className="gait-live-videos">
        <div className="gait-live-video-wrap">
          <p className="gait-live-label">{t('gait.live_local')}</p>
          <video ref={localVideoRef} autoPlay muted playsInline className="gait-live-video" />
        </div>
        <div className="gait-live-video-wrap">
          <p className="gait-live-label">{t('gait.live_remote')}</p>
          <video ref={remoteVideoRef} autoPlay playsInline className="gait-live-video" />
        </div>
      </div>

      {error && <div className="gait-error">{error}</div>}

      <div className="gait-live-actions">
        <button className="btn-ghost" onClick={handleClose}>{t('gait.live_back')}</button>

        {status === 'idle' && (
          <button className="btn-primary" onClick={start}>{t('gait.live_start')}</button>
        )}
        {status === 'connecting' && (
          <button className="btn-secondary" disabled>
            <span className="spinner dark" /> {t('gait.live_connecting')}
          </button>
        )}
        {status === 'live' && (
          <button className="btn-secondary" onClick={stop}>{t('gait.live_stop')}</button>
        )}
        {status === 'error' && (
          <button className="btn-primary" onClick={start}>{t('gait.live_retry')}</button>
        )}
      </div>
    </section>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────

export default function GaitAnalysisPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const consultationId = searchParams.get('consultation_id') || '';
  const qs = consultationId ? `?consultation_id=${consultationId}` : '';
  const readOnly = !!consultationId;

  const [videoBack, setVideoBack] = useState(null);
  const [videoSide, setVideoSide] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [showLive, setShowLive] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(readOnly);
  // Évite tout setState après démontage pendant le sondage (cf. handleAnalyze)
  // -- le praticien peut naviguer ailleurs pendant les 1-3 min de traitement.
  const cancelledRef = useRef(false);
  useEffect(() => () => { cancelledRef.current = true; }, []);

  // Consultation historique : charge le résultat déjà enregistré (vidéos +
  // séries temporelles) pour CETTE consultation précise, en lecture seule --
  // pas d'upload possible, contrairement au flux normal ci-dessous qui
  // n'affiche jamais rien tant qu'on n'a pas lancé une nouvelle analyse.
  useEffect(() => {
    if (!readOnly) return;
    setHistoryLoading(true);
    fetch(`${API}/api/patients/${id}${qs}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(async data => {
        const gait = data.gait_analysis || {};
        const views = Object.keys(gait).filter(v => v === 'frontal' || v === 'sagittal');
        if (views.length === 0) { setResult(null); return; }
        const entries = await Promise.all(views.map(async v => {
          try {
            const r = await fetch(`${API}/api/patients/${id}/gait-data/${v}${qs}`);
            if (!r.ok) return [v, null];
            return [v, await r.json()];
          } catch (e) { return [v, null]; }
        }));
        const merged = {};
        entries.forEach(([v, d]) => { if (d) merged[v] = d; });
        setResult(Object.keys(merged).length ? merged : null);
      })
      .catch(() => setResult(null))
      .finally(() => setHistoryLoading(false));
  }, [id, consultationId, readOnly]);

  const hasAtLeastOne = !!videoBack || !!videoSide;

  // Le traitement (YOLO + pose estimation, 1-3 min selon la durée des
  // vidéos) tourne maintenant en tâche de fond côté backend (cf.
  // _run_gait_analysis_bg) -- l'appel POST ne fait plus qu'uploader les
  // vidéos brutes et répond immédiatement ({"status": "queued"}). Avant ce
  // correctif, la requête restait ouverte tout le temps du traitement et se
  // faisait couper (504) par le gateway/proxy sur les vidéos les plus
  // longues, avant même que le backend ait fini. On sonde maintenant
  // /gait-data/{view} (même endpoint que la relecture historique ci-dessus)
  // jusqu'à ce que chaque vue attendue soit disponible.
  const handleAnalyze = async () => {
    if (!hasAtLeastOne) {
      setError(t('gait.error_no_video'));
      return;
    }
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      if (videoBack) fd.append('video_back', videoBack);
      if (videoSide) fd.append('video_side', videoSide);

      const res = await fetch(`${API}/api/patients/${id}/gait-analysis`, {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) {
        // Le backend renvoie un détail précis (crédits, abonnement, vidéo
        // manquante...) dans le corps JSON -- on le remonte plutôt que le
        // message générique, pour ne plus jamais avoir à deviner la cause.
        let detail = t('gait.error_analysis');
        try {
          const body = await res.json();
          if (body?.detail) detail = body.detail;
        } catch (e) { /* corps non-JSON : on garde le message générique */ }
        throw new Error(detail);
      }

      const expectedViews = [];
      if (videoBack) expectedViews.push('frontal');
      if (videoSide) expectedViews.push('sagittal');

      const pollIntervalMs = 5000;
      const maxAttempts = 60; // ~5 minutes, même ordre de grandeur que le sondage des overlays pieds/posture
      let attempt = 0;

      const poll = async () => {
        if (cancelledRef.current) return;
        attempt += 1;
        const entries = await Promise.all(expectedViews.map(async v => {
          try {
            const r = await fetch(`${API}/api/patients/${id}/gait-data/${v}${qs}`);
            if (!r.ok) return [v, null];
            return [v, await r.json()];
          } catch (e) { return [v, null]; }
        }));
        if (cancelledRef.current) return;
        const merged = {};
        entries.forEach(([v, d]) => { if (d) merged[v] = d; });

        if (Object.keys(merged).length === expectedViews.length) {
          setResult(merged);
          setUploading(false);
          return;
        }
        if (attempt >= maxAttempts) {
          setError(t('gait.error_analysis'));
          setUploading(false);
          return;
        }
        setTimeout(poll, pollIntervalMs);
      };
      poll();
    } catch (e) {
      setError(e.message);
      setUploading(false);
    }
  };

  // ── Données graphiques ──────────────────────────────────────────────────

  const frontalData = result?.frontal
    ? buildChartData(result.frontal.timeseries, [
        'valgus_varus_idx', 'pelvic_obliquity',
        'femur_angle_left', 'femur_angle_right',
        'tibia_angle_left', 'tibia_angle_right',
      ])
    : [];

  const sagittalData = result?.sagittal
    ? buildChartData(result.sagittal.timeseries, [
        'knee_left', 'knee_right', 'ankle_left', 'ankle_right',
      ])
    : [];

  const phaseData = result?.sagittal
    ? buildPhaseData(result.sagittal.timeseries, result.sagittal.contacts)
    : [];

  const fs = result?.frontal?.summary;
  const ss = result?.sagittal?.summary;

  const hasFrontal  = !!result?.frontal;
  const hasSagittal = !!result?.sagittal;
  const hasResult   = hasFrontal || hasSagittal;

  return (
    <div className="gait-page">
      <header className="gait-header">
        <button className="back-btn" onClick={() => navigate(`/analysis/${id}${qs}`)}>
          {t('gait.back_to_postural')}
        </button>
        <div className="gait-title-row">
          <div>
            <p className="gait-eyebrow">{t('gait.eyebrow')}</p>
            <h1 className="gait-title">{t('gait.title')}</h1>
          </div>
        </div>
      </header>

      {readOnly && (
        <div className="historical-banner">{t('gait.readonly_banner')}</div>
      )}

      {readOnly && historyLoading && (
        <section className="gait-upload-section">
          <p className="gait-upload-title">{t('gait.analyzing')}</p>
        </section>
      )}

      {readOnly && !historyLoading && !hasResult && (
        <section className="gait-upload-section">
          <p className="gait-upload-title">{t('gait.no_data_historical')}</p>
        </section>
      )}

      {!readOnly && !result && showLive && (
        <LiveCaptureSection patientId={id} t={t} onClose={() => setShowLive(false)} />
      )}

      {!readOnly && !result && !showLive && (
        <section className="gait-upload-section">
          <p className="gait-upload-title">
            {t('gait.upload_intro')}
          </p>
          <div className="gait-upload-grid">
            <UploadSlot
              label={t('gait.upload_back_label')}
              hint={t('gait.upload_back_hint')}
              value={videoBack}
              onChange={setVideoBack}
              t={t}
            />
            <UploadSlot
              label={t('gait.upload_side_label')}
              hint={t('gait.upload_side_hint')}
              value={videoSide}
              onChange={setVideoSide}
              t={t}
            />
          </div>
          {error && <div className="gait-error">{error}</div>}
          <div className="gait-upload-actions">
            <button
              className="btn-primary"
              onClick={handleAnalyze}
              disabled={uploading || !hasAtLeastOne}
            >
              {uploading
                ? <><span className="spinner dark" /> {t('gait.analyzing')}</>
                : t('gait.analyze_btn')}
            </button>
            <button
              className="btn-secondary"
              onClick={() => setShowLive(true)}
              disabled={uploading}
            >
              {t('gait.live_title')}
            </button>
            <button
              className="btn-secondary"
              onClick={() => navigate(`/clinical-summary/${id}`)}
              disabled={uploading}
            >
              {t('gait.skip_btn')}
            </button>
          </div>
        </section>
      )}

      {hasResult && (
        <>
          {typeof result?.duration_ms === 'number' && (
            <div className="gait-timing-note">
              {t('gait.ai_duration', {
                time: formatDuration(result.duration_ms),
                frames: (fs?.frames_processed || 0) + (ss?.frames_processed || 0),
              })}
            </div>
          )}
          {/* ── Analyse frontale (vue de dos) ── */}
          {hasFrontal && (
            <VideoSection
              pid={id}
              qs={qs}
              view="frontal"
              title={t('gait.frontal_title')}
              subtitle={t('gait.frontal_subtitle')}
              t={t}
              summaryStats={[
                { label: t('gait.stat_cadence'), value: fs.cadence_mean, unit: ' pas/min' },
                { label: t('gait.stat_asymmetry'), value: fs.asymmetry_mean, unit: ' %', warn: fs.asymmetry_mean > 15 },
                { label: t('gait.stat_valgus_varus_mean'), value: fs.valgus_varus_mean, unit: ' px', warn: Math.abs(fs.valgus_varus_mean) > 15 },
                { label: t('gait.stat_valgus_varus_max'), value: fs.valgus_varus_max_abs, unit: ' px', warn: fs.valgus_varus_max_abs > 15 },
                { label: t('gait.stat_pelvic_obliquity_mean'), value: fs.pelvic_obliquity_mean, unit: '°', warn: Math.abs(fs.pelvic_obliquity_mean) > 5 },
                { label: t('gait.stat_contacts'), value: `${fs.total_contacts_left} / ${fs.total_contacts_right}`, unit: '' },
              ]}
              charts={[
                {
                  title: t('gait.chart_valgus_varus'),
                  data: frontalData,
                  refLines: [15, -15],
                  lines: [{ key: 'valgus_varus_idx', name: t('gait.chart_valgus_varus_legend'), color: '#ff4488' }],
                },
                {
                  title: t('gait.chart_pelvic_obliquity'),
                  data: frontalData,
                  refLines: [5, -5],
                  lines: [{ key: 'pelvic_obliquity', name: t('gait.chart_pelvic_obliquity_legend'), color: '#88ff44' }],
                },
                {
                  title: t('gait.chart_femur_angle'),
                  data: frontalData,
                  refLines: [8, -8],
                  lines: [
                    { key: 'femur_angle_left', name: t('gait.legend_left'), color: '#ff9500' },
                    { key: 'femur_angle_right', name: t('gait.legend_right'), color: '#00aaff' },
                  ],
                },
                {
                  title: t('gait.chart_tibia_angle'),
                  data: frontalData,
                  refLines: [8, -8],
                  lines: [
                    { key: 'tibia_angle_left', name: t('gait.legend_left'), color: '#ff9500' },
                    { key: 'tibia_angle_right', name: t('gait.legend_right'), color: '#00aaff' },
                  ],
                },
              ]}
            />
          )}

          {hasFrontal && hasSagittal && <div className="gait-divider" />}

          {/* ── Analyse sagittale (vue de profil) ── */}
          {hasSagittal && (
            <VideoSection
              pid={id}
              qs={qs}
              view="sagittal"
              title={t('gait.sagittal_title')}
              subtitle={t('gait.sagittal_subtitle')}
              t={t}
              summaryStats={[
                { label: t('gait.stat_cadence'), value: ss.cadence_mean, unit: ' pas/min' },
                { label: t('gait.stat_asymmetry'), value: ss.asymmetry_mean, unit: ' %', warn: ss.asymmetry_mean > 15 },
                { label: t('gait.stat_knee_left_range'), value: ss.knee_left_range, unit: '°' },
                { label: t('gait.stat_knee_right_range'), value: ss.knee_right_range, unit: '°' },
                { label: t('gait.stat_stride_left'), value: ss.stride_duration_left_mean, unit: ' s' },
                { label: t('gait.stat_stride_right'), value: ss.stride_duration_right_mean, unit: ' s' },
              ]}
              charts={[
                {
                  title: t('gait.chart_knee_flexion'),
                  data: sagittalData,
                  refLines: [90],
                  lines: [
                    { key: 'knee_left', name: t('gait.legend_left'), color: '#ff9500' },
                    { key: 'knee_right', name: t('gait.legend_right'), color: '#00aaff' },
                  ],
                },
                {
                  title: t('gait.chart_ankle_angle'),
                  data: sagittalData,
                  refLines: [10],
                  lines: [
                    { key: 'ankle_left', name: t('gait.legend_left'), color: '#ff9500' },
                    { key: 'ankle_right', name: t('gait.legend_right'), color: '#00aaff' },
                  ],
                },
                {
                  title: t('gait.chart_stance_phases'),
                  data: phaseData,
                  yDomain: [0, 1.2],
                  yTicks: [0, 1],
                  stepped: true,
                  lines: [
                    { key: 'stance_left', name: t('gait.legend_left'), color: '#ff9500' },
                    { key: 'stance_right', name: t('gait.legend_right'), color: '#00aaff' },
                  ],
                },
              ]}
            />
          )}

          <div className="gait-restart">
            <button className="btn-secondary" onClick={() => { setResult(null); setVideoBack(null); setVideoSide(null); }}>
              {t('gait.restart_btn')}
            </button>
            <button className="btn-primary" onClick={() => navigate(`/clinical-summary/${id}`)}>
              {t('gait.clinical_summary_btn')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}